import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Text,
  Image,
} from 'react-native';
import { useNavigationState, useNavigation } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons, Feather } from '@expo/vector-icons';
import { COLORS, LAYOUT, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

// ─── Route helpers ──────────────────────────────────────────────────────────
const getActiveRouteName = (state) => {
  if (!state || !state.routes || typeof state.index !== 'number') return '';
  const route = state.routes[state.index];
  return route?.state ? getActiveRouteName(route.state) : (route?.name ?? '');
};

// ─── Image Icon Renderer ─────────────────────────────────────────────────────
const TabImageIcon = ({ source, active }) => (
  <Image
    source={source}
    style={[styles.tabIcon, { tintColor: active ? '#FFFFFF' : '#9CA3AF' }]}
    resizeMode="contain"
  />
);

// ─── Vector Icon Renderer ────────────────────────────────────────────────────
const TabVectorIcon = ({ lib, name, active }) => {
  const color = active ? '#FFFFFF' : '#9CA3AF';
  if (lib === 'MaterialCommunity') {
    return <MaterialCommunityIcons name={name} size={18} color={color} />;
  }
  if (lib === 'Feather') {
    return <Feather name={name} size={18} color={color} />;
  }
  return <Ionicons name={name} size={18} color={color} />;
};

// ─── Tab Configuration ───────────────────────────────────────────────────────
const TABS = [
  {
    key: 'home',
    label: 'Home',
    altKeys: ['home', 'Home', 'Root', 'Dashboard'],
    renderIcon: (active) => (
      <TabImageIcon active={active} source={require('../../assets/icons/Home.png')} />
    ),
  },
  {
    key: 'Properties',
    label: 'Properties',
    altKeys: [
      'Properties',
      'PropertyDetails',
      'PropertyInfo',
      'PropertySection',
      'PropertyAddress',
      'PropertiesInfoDetails',
      'Certification',
      'Licensing',
      'Requirements',
    ],
    renderIcon: (active) => (
      <TabImageIcon active={active} source={require('../../assets/icons/Properties.png')} />
    ),
  },
  {
    key: 'Tenants',
    label: 'Tenancies',
    altKeys: [
      'Tenants',
      'TeanantOnboarding',
      'TeanantInfo',
      'CurrentOnboards',
      'CompletedOnboards',
      'ArchivedOnboards',
      'TenantDetails',
      'PropertyTenantDetails',
    ],
    renderIcon: (active) => (
      <TabImageIcon active={active} source={require('../../assets/icons/Tenants.png')} />
    ),
  },
  {
    key: 'Payments',
    label: 'Payments',
    altKeys: ['Payments', 'PaymentDetails', 'PaymentIncomingDetails', 'PaymentOutgoingDetails', 'MyInvoices', 'BankAccount'],
    renderIcon: (active) => (
      <TabImageIcon active={active} source={require('../../assets/icons/Payments.png')} />
    ),
  },
  {
    key: '__more__',
    label: 'More',
    altKeys: [
      'Setting',
      'Reminder',
      'MyDocs',
      'MediaHub',
      'MaintenanceProject',
      'AccountEdit',
      'ChangePassword',
      'MyAccount',
      'ContactUs',
      'LegalBits',
      'ArchiveManager',
      'HubFlow',
    ],
    renderIcon: (active) => (
      <TabVectorIcon lib="Feather" name="grid" active={active} />
    ),
  },
];

// ─── More Popup Menu Items ───────────────────────────────────────────────────
const MORE_ITEMS = [
  {
    key: 'MaintenanceProject',
    label: 'Maintenance',
    iconType: 'vector',
    iconLib: 'MaterialCommunity',
    iconName: 'tools',
  },
  {
    key: 'Reminder',
    label: 'Reminders',
    iconType: 'image',
    iconSource: require('../../assets/icons/Reminder.png'),
  },
  {
    key: 'MyDocs',
    label: 'Documents',
    iconType: 'image',
    iconSource: require('../../assets/icons/My Docs purple.png'),
  },
  {
    key: 'MediaHub',
    label: 'Media Hub',
    iconType: 'image',
    iconSource: require('../../assets/icons/MediaHub.png'),
  },
  {
    key: 'Setting',
    label: 'Settings',
    iconType: 'vector',
    iconLib: 'Ionicons',
    iconName: 'settings-outline',
  },
];

const MoreMenu = ({ visible, onClose, onNavigate, activeRoute }) => {
  if (!visible) return null;
  return (
    <>
      <TouchableOpacity style={styles.moreOverlay} activeOpacity={1} onPress={onClose} />
      <View style={styles.moreMenu}>
        {MORE_ITEMS.map((item) => {
          const isActive = activeRoute === item.key;
          return (
            <TouchableOpacity
              key={item.key}
              style={[styles.moreMenuItem, isActive && styles.moreMenuItemActive]}
              onPress={() => {
                onNavigate(item.key);
                onClose();
              }}
              activeOpacity={0.7}
            >
              <View style={[styles.moreMenuIconWrap, isActive && styles.moreMenuIconWrapActive]}>
                {item.iconType === 'image' ? (
                  <Image
                    source={item.iconSource}
                    style={[styles.moreMenuIconImage, { tintColor: isActive ? '#FFFFFF' : '#6B7280' }]}
                    resizeMode="contain"
                  />
                ) : item.iconLib === 'MaterialCommunity' ? (
                  <MaterialCommunityIcons name={item.iconName} size={16} color={isActive ? '#FFFFFF' : '#6B7280'} />
                ) : (
                  <Ionicons name={item.iconName} size={16} color={isActive ? '#FFFFFF' : '#6B7280'} />
                )}
              </View>
              <Text style={[styles.moreMenuText, isActive && styles.moreMenuTextActive]}>
                {item.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </>
  );
};

// ─── Main BottomTabBar Component ─────────────────────────────────────────────
const BottomTabBar = () => {
  const navigation = useNavigation();
  const activeRoute = getActiveRouteName(
    useNavigationState((state) => state)
  );
  const [moreVisible, setMoreVisible] = useState(false);

  const isTabActive = (tab) => {
    return tab.key === activeRoute || (tab.altKeys && tab.altKeys.includes(activeRoute));
  };

  const handleTabPress = (tab) => {
    if (tab.key === '__more__') {
      setMoreVisible((v) => !v);
      return;
    }
    setMoreVisible(false);
    navigation.navigate(tab.key);
  };

  return (
    <>
      <MoreMenu
        visible={moreVisible}
        onClose={() => setMoreVisible(false)}
        onNavigate={(key) => navigation.navigate(key)}
        activeRoute={activeRoute}
      />
      <View style={styles.container}>
        {TABS.map((tab) => {
          const active = isTabActive(tab);
          return (
            <TouchableOpacity
              key={tab.key}
              style={styles.tab}
              onPress={() => handleTabPress(tab)}
              activeOpacity={0.7}
            >
              <View style={[styles.iconWrapper, active && styles.iconWrapperActive]}>
                {tab.renderIcon(active)}
              </View>
              <Text style={[styles.tabLabel, active && styles.tabLabelActive]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </>
  );
};

export default BottomTabBar;

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: LAYOUT.bottomTabHeight,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingBottom: Platform.OS === 'ios' ? 8 : 0,
    zIndex: 1000,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    ...SHADOWS.sm,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    height: '100%',
  },
  iconWrapper: {
    width: 40,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
  },
  iconWrapperActive: {
    backgroundColor: '#6C2BD9',
  },
  tabIcon: {
    width: 20,
    height: 20,
  },
  tabLabel: {
    fontSize: 11,
    fontWeight: '500',
    color: '#9CA3AF',
    marginTop: 2,
  },
  tabLabelActive: {
    color: '#6C2BD9',
    fontWeight: '700',
  },

  /* MORE POPUP MENU */
  moreOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 1001,
  },
  moreMenu: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: LAYOUT.bottomTabHeight + 10,
    right: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 8,
    paddingHorizontal: 6,
    minWidth: 180,
    zIndex: 1002,
    ...SHADOWS.md,
  },
  moreMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: BORDER_RADIUS.md,
  },
  moreMenuItemActive: {
    backgroundColor: '#F5F3FF',
  },
  moreMenuIconWrap: {
    width: 26,
    height: 26,
    borderRadius: 6,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  moreMenuIconWrapActive: {
    backgroundColor: '#6C2BD9',
  },
  moreMenuIconImage: {
    width: 16,
    height: 16,
  },
  moreMenuText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  moreMenuTextActive: {
    color: '#6C2BD9',
    fontWeight: '700',
  },
});
